import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MailCheck, Send } from 'lucide-react';
import { ApiError, api } from '@/lib/api';
import { Button } from '@/components/ui';

/**
 * Requests a fresh verification link.
 *
 * Offered wherever someone can discover their account is unverified: the
 * confirmation screen after registering, and the sign-in error that tells them
 * to check their inbox. Before this there was no way to recover from a
 * verification email that never arrived — and the expiry message already told
 * people to "request a new one".
 */
export function ResendVerification({ email }: { email: string }) {
  const [sent, setSent] = useState(false);

  const resend = useMutation({
    mutationFn: () => api.post<{ message: string }>('/auth/resend-verification', { email }),
    onSuccess: (result) => {
      setSent(true);
      toast.success(result.message);
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not send a new link.'),
  });

  if (sent) {
    return (
      <p className="flex items-center justify-center gap-2 text-sm text-success">
        <MailCheck size={15} /> A new link is on its way.
      </p>
    );
  }

  return (
    <Button
      variant="outline"
      full
      // A solid background, because this sits inside a tinted alert where a
      // transparent button disappears into the error.
      className="border-danger/25 bg-background hover:bg-background/80"
      size="sm"
      loading={resend.isPending}
      disabled={!email}
      onClick={() => resend.mutate()}
    >
      <Send size={15} /> Send the link again
    </Button>
  );
}
